import { Option, ReadonlyArray, Runtime, pipe } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import * as Driver from "~/Driver";
import { ID } from "~/ID";
import { Block } from "~/Layout";
import { Location } from "~/Location";
import { Remote } from "~/Remote";
import { Request as GenericRequest } from "~/Request";
import { Time } from "~/Time";

import { Order } from "../Order";

export type NoBranchDisplay = "No Branch";

export const noBranchDisplay = (): NoBranchDisplay => "No Branch";

export type Branch = { readonly branch: ID & Partial<Location> };
export namespace Branch {
  export namespace Response {
    export const definition = () =>
      Runtime.intersection([
        Location.Response.definition(),
        Runtime.strict({ id: Runtime.number() }),
      ]);

    export const decode = (
      encoded: Runtime.ToType<typeof definition>
    ): Branch => ({
      branch: {
        ...ID.from(encoded.id),
        ...Location.Response.decode(encoded),
      },
    });
  }

  export namespace FromLocation {
    export const use = (location?: Partial<ID>): Remote<Option<Branch>> =>
      pipe(
        Branches.use(),
        Remote.map(
          flow(
            ({ branches }) => branches,
            ReadonlyArray.findFirst<Branch>(
              ({ branch }) => branch.location?.id === location?.id
            )
          )
        )
      );

    export const useOrUndefined = (
      ...args: Parameters<typeof use>
    ): Branch | undefined =>
      pipe(use(...args), Remote.map(Option.toUndefined), Remote.toUndefined);
  }
}

export type Branches = { readonly branches: readonly Branch[] };
export namespace Branches {
  export const use = (): Remote<Branches> => Request.use();

  export namespace Request {
    /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/develop/index.html?highlight=companies#get--companies-(int-company_id)-branches) */
    export const use = (): Remote<Branches> =>
      pipe(
        Remote.Query.use(
          "branches",
          flow(
            GenericRequest.fromConfig({
              endpoint: "/branches",
              headers: { "X-Fields": "{id location_id}" },
            }),

            GenericRequest.Expect.status(200),
            GenericRequest.Expect.json(Response.definition()),
            GenericRequest.onSuccess(Response.decode)
          ),
          {
            staleTime: Time.Duration.minutes(15).duration,
            keepPreviousData: true,
          }
        )
      );
  }

  export namespace Response {
    export const definition = () => Runtime.array(Branch.Response.definition());
    export const decode = (
      encoded: Runtime.ToType<typeof definition>
    ): Branches => ({
      branches: pipe(encoded, ReadonlyArray.map(Branch.Response.decode)),
    });
  }
}

/**
 * Namespace that handles Branches attached to requests in other entities.
 */
export namespace NewBranch {
  export type Branch = {
    readonly branch_id: number;
    readonly name?: string;
  };

  export type Props = {
    readonly driver?: Driver.Driver;
  };

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        branch_id: Runtime.number(),
        name: Runtime.nullable(Runtime.string()),
      });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Branch => {
      return {
        branch_id: response.branch_id,
        name: response.name ?? noBranchDisplay(),
      };
    };
  }

  export const name = (branch: NewBranch.Branch | undefined) =>
    branch?.name ?? noBranchDisplay();

  export const BranchName = (branch: {
    readonly branch?: NewBranch.Branch;
  }) => <>{name(branch.branch)}</>;

  export const order = () =>
    Order.fromCompare<NewBranch.Branch | undefined>((a, b) => {
      return Order.string().compare(name(a), name(b));
    });

  export const { equals, compare } = order();

  const overflow = (s: string) => (s.length > 35 ? `${s.slice(0, 35)}...` : s);

  export const Name = ({ branch }: { readonly branch?: Branch }) =>
    pipe(overflow(branch?.name ?? noBranchDisplay()), (name) => <>{name}</>);

  export const TooltipBody = styled.div`
    padding: 0.2em;
    font-size: 10pt;
    font-weight: 700;
  `;

  export const TooltipTitle = (title: string, name: string) => (
    <span data-testid="branch-location-name-tooltip">
      <Block.Component>{title}</Block.Component>
      <TooltipBody>{name}</TooltipBody>
    </span>
  );
}
